"use client";

import { Dialog, DialogContent } from "../../shadcn/ui/dialog";
import React, { ReactNode, useEffect, useState } from "react";

import Image from "next/image";
import { PunchupVerifiedBadge } from "../../components/common";
import { ShowWithComedian } from "@punchup/data/legacy";
import dayjs from "@punchup/utils/dayjs";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";

interface Performer {
  name: string;
  imageUrl: string;
  isVerified: boolean;
}

interface WaitlistModalBaseProps {
  show: ShowWithComedian;
  open?: boolean;
  onClose: () => void;
  badgeIcon: ReactNode;
  children: ReactNode;
}

const WaitlistModalBase: React.FC<WaitlistModalBaseProps> = ({
  show,
  open = true,
  onClose,
  badgeIcon,
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [performers, setPerformers] = useState<Performer[]>([]);

  const datetime =
    typeof show.datetime === "string"
      ? dayjs(show.datetime)
      : show.datetime || dayjs();

  useEffect(() => {
    const loadPerformers = async () => {
      setLoading(true);

      if (show.show_comedians && show.show_comedians.length > 0) {
        const performers = show.show_comedians.map((comedian) => ({
          name: comedian.display_name || "",
          imageUrl: getCDNImageURL(comedian.about_img || "") || "",
          isVerified: comedian.is_punchup_verified || false,
        }));
        setPerformers(performers);
        setLoading(false);
        return;
      }

      setLoading(false);
    };
    loadPerformers();
  }, [show.comedian_id, show.show_comedians]);

  const MAX_AVATARS = 3;
  const displayedPerformers = performers.slice(0, MAX_AVATARS);
  const remainingCount = performers.length - MAX_AVATARS;
  const hasOverflow = remainingCount > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen: boolean) => !isOpen && onClose()}
    >
      <DialogContent
        className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-8 border-none"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
        }}
      >
        <div>
          {performers.length > 0 ? (
            <>
              <div className="flex justify-center mb-4">
                <div className="relative flex justify-center">
                  {displayedPerformers.map((performer, index) => (
                    <div
                      key={index}
                      className={`relative ${index > 0 ? "-ml-4" : ""}`}
                      style={{ zIndex: MAX_AVATARS - index + 1 }}
                    >
                      <Image
                        src={performer.imageUrl}
                        alt={performer.name}
                        width={96}
                        height={96}
                        className="w-24 h-24 rounded-full"
                      />
                      {performer.isVerified && (
                        <div className="absolute bottom-0 right-0">
                          <PunchupVerifiedBadge className="w-7 h-7 md:w-10 md:h-10" />
                        </div>
                      )}
                      {!performer.isVerified &&
                        index === displayedPerformers.length - 1 &&
                        !hasOverflow && (
                          <div className="absolute bottom-0 right-0">
                            {badgeIcon}
                          </div>
                        )}
                    </div>
                  ))}
                  {hasOverflow && (
                    <div className="relative -ml-4" style={{ zIndex: 0 }}>
                      <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-lg font-semibold text-gray-700">
                          +{remainingCount}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {performers
                    .map((p, index, arr) => {
                      let separator = "";
                      if (index === arr.length - 1) {
                        // Last performer - no separator
                        separator = "";
                      } else if (index === arr.length - 2) {
                        // Second-to-last performer - use " & "
                        separator = " & ";
                      } else {
                        // All others - use comma
                        separator = ", ";
                      }
                      return `${p.name}${separator}`;
                    })
                    .join("")}
                </h2>
                <p className="text-gray-500">
                  {datetime.format("dddd MMM DD | h:mmA")}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-4">{badgeIcon}</div>

              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {show.title || show.venue}
                </h2>
                <p className="text-gray-500">
                  {datetime.format("dddd MMM DD | h:mmA")}
                </p>
              </div>
            </>
          )}

          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModalBase;
